import React from 'react';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';

interface Brand {
  name: string;
  rankScore: number | null;
  namedFirstRate: number | null;
  inclusionRate: number | null;
  meanPosition: number | null;
  isBrand: boolean;
  // False for a brand the models put ahead of you that is not in your set.
  configured?: boolean;
}

interface Props {
  headline: string;
  summary?: string | null;
  // The recommendation snapshot for the right-hand column.
  namedFirst: number | null;
  inTheSix: number | null;
  meanPosition: number | null;
  memoryNamedFirst: number | null;
  memoryInTheSix: number | null;
  order: Brand[];
  totalAnswers: number | null;
  searchedAnswers: number | null;
  onFixes?: () => void;
}

const pct = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : `${Math.round(v * 1000) / 10}%`;
const two = (v: number | null | undefined): string =>
  v === null || v === undefined ? '–' : v.toFixed(2);

const Big: React.FunctionComponent<{ value: string; caption: React.ReactNode }> = ({ value, caption }) => (
  <div>
    <div style={{ fontSize: 36, fontWeight: 800, color: THEME.textBase, lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: 12.5, color: THEME.textSecondary, marginTop: 6, lineHeight: 1.4 }}>{caption}</div>
  </div>
);

// The top of the read, in the Briefing hero's shape: the one thing on the
// left, the numbers that back it on the right.
export const ReadHero: React.FunctionComponent<Props> = ({
  headline,
  summary,
  namedFirst,
  inTheSix,
  meanPosition,
  memoryNamedFirst,
  memoryInTheSix,
  order,
  totalAnswers,
  searchedAnswers,
  onFixes,
}) => (
  <Card pad={0} style={{ overflow: 'hidden' }}>
    <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr' }}>
      <div style={{ padding: '28px 30px 26px' }}>
        <Eyebrow color={THEME.primaryDark} style={{ marginBottom: 14 }}>
          If you only know one thing
        </Eyebrow>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            lineHeight: 1.3,
            color: THEME.textBase,
            letterSpacing: '-0.01em',
            margin: '0 0 12px',
          }}
        >
          {headline}
        </h1>
        {summary && (
          <p style={{ fontSize: 15, lineHeight: 1.6, color: THEME.textSecondary, margin: '0 0 20px', maxWidth: '62ch' }}>
            {summary}
          </p>
        )}
        {onFixes && (
          <button
            type="button"
            onClick={onFixes}
            style={{
              background: THEME.primary,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 18px',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Jump straight to Fix the record
          </button>
        )}
      </div>

      <div
        style={{
          borderLeft: `1px solid ${THEME.borderSubtle}`,
          background: THEME.bgTinted,
          padding: '28px 30px 26px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <Eyebrow>Would the models recommend you?</Eyebrow>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Big
            value={pct(inTheSix)}
            caption={
              <>
                of {searchedAnswers ?? 'the'} answers with web search listed you among six brands to consider
                {meanPosition !== null ? `, at position ${two(meanPosition)} of six on average` : ''}
              </>
            }
          />
          <Big value={pct(namedFirst)} caption="named you first" />
        </div>
        {(memoryInTheSix !== null || memoryNamedFirst !== null) && (
          <div
            style={{
              fontSize: 12.5,
              color: THEME.textSecondary,
              lineHeight: 1.45,
              borderTop: `1px solid ${THEME.borderSubtle}`,
              paddingTop: 12,
            }}
          >
            <strong style={{ color: THEME.textBase }}>From memory, without searching:</strong>{' '}
            {pct(memoryInTheSix)} listed you and {pct(memoryNamedFirst)} named you first.{' '}
            {inTheSix !== null && memoryInTheSix !== null
              ? inTheSix > memoryInTheSix + 0.05
                ? 'What the models read on the web is earning you recommendations.'
                : inTheSix < memoryInTheSix - 0.05
                  ? 'What the models read on the web is costing you recommendations.'
                  : 'Searching changes little.'
              : ''}
          </div>
        )}
        {order.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: THEME.textMuted,
                marginBottom: 8,
              }}
            >
              Who they name, in order
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {order.map((row, i) => (
                <div
                  key={row.name}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '20px minmax(0, 1fr) auto',
                    // Baseline, not top: the score is a larger face than the
                    // name, and top-aligned it floats a pixel or two high.
                    alignItems: 'baseline',
                    columnGap: 10,
                    color: row.isBrand ? THEME.textBase : THEME.textSecondary,
                  }}
                >
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 6,
                      background: row.isBrand ? THEME.primary : THEME.bgChip,
                      color: row.isBrand ? '#fff' : THEME.textSecondary,
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      alignSelf: 'start',
                    }}
                  >
                    {i + 1}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13.5,
                        fontWeight: row.isBrand ? 700 : 500,
                        lineHeight: '20px',
                      }}
                    >
                      {row.name}
                      {row.configured === false && (
                        <span
                          style={{
                            marginLeft: 8,
                            fontSize: 10.5,
                            fontWeight: 600,
                            color: THEME.textMuted,
                            background: THEME.bgChip,
                            borderRadius: 999,
                            padding: '1px 7px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          not in your set
                        </span>
                      )}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: THEME.textMuted, lineHeight: 1.4 }}>
                      first {pct(row.namedFirstRate)} · listed {pct(row.inclusionRate)} · position {two(row.meanPosition)}
                    </span>
                  </span>
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      lineHeight: '20px',
                      color: row.isBrand ? THEME.textBase : THEME.textSecondary,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {two(row.rankScore)}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11.5, color: THEME.textMuted, marginTop: 10, lineHeight: 1.45 }}>
              The bold number is the rank score: 7 for always first, down to 1 for never listed, averaged over
              every answer with web search, so a brand in nearly every list outranks one named first more often
              but left out more often.
              {order.some((row) => row.configured === false)
                ? ' A brand marked not in your set is one the models put ahead of you that you did not name as a competitor.'
                : ''}
              {totalAnswers !== null ? ` From ${totalAnswers} answers to a question that never named you.` : ''}
            </div>
          </div>
        )}
      </div>
    </div>
  </Card>
);
